import { formatSn } from '@oskecare/fe-utilities/scripts/format';
import { OstCache } from './models/OstCache.js';
import Mutex from './Mutex.js';
import DataSharingParser from './DataSharingParser.js';
import OstRequest from './OstRequest.js';
import Parser from './Parser.js';
import ReverseParser from './ReverseParser.js';
import OstCacheManager from './OstCacheManager.js';

/**
 * Ost library to handle operations with OST.
 *
 * @property {Logger} _logger
 * @property {CustomerDataManager} _customerDataManager
 * @property {?Promise<void>} _cookiePromise
 * @property {DataSharingParser} _dataSharingParser
 * @property {OstRequest} _ostRequest
 * @property {OstCacheManager} _ostCacheManager
 * @property {Map<string, [string, Map<string, string[]>]>} _basketLogCache
 * @property {Map<string, Mutex>} _mutexList
 */
export default class Ost {
  /**
   * @param {object} config
   * @param {Logger} logger
   * @param {CustomerDataManager} customerDataManager
   * @param {Products} products
   * @param {DataSharing} dataSharing
   */
  constructor({ config, logger, customerDataManager, dataSharing, products }) {
    this._logger = logger;
    this._customerDataManager = customerDataManager;

    this._cookiePromise = null;

    // Instantiate sub-modules
    this._dataSharingParser = new DataSharingParser(
      config,
      dataSharing,
      customerDataManager,
      logger,
    );
    this._ostRequest = new OstRequest({ config, logger, customerDataManager, products });
    this._ostCacheManager = new OstCacheManager();
    this._basketLogCache = new Map();

    this._mutexList = new Map();
  }

  /**
   * Temporarily uses `setOstCookie` method, which needs to be called
   * only once here to ensure execution before any other requests to OST API.
   * This sets `NODESESSIONID` cookie.
   *
   * IMPORTANT: Only initialize after the AuthManager successfully initialized
   *
   * @calls { GET; {ostApiUrl}/ping; OFFER_API }
   * @returns {void}
   */
  initialize() {
    this._cookiePromise = this._ostRequest.setOstCookie();
  }

  /**
   * Checks if the cookie promise have been set (should be set during initialization) and returns it.
   *
   * @return {Promise<void>}
   * @private
   */
  _checkCookiePromise() {
    if (this._cookiePromise === null) {
      throw new Error('Module was not properly initialized.');
    }
    return this._cookiePromise;
  }

  /**
   *
   * @calls { GET; {ostApiUrl}/get-init-with-catalogue; OFFER_API }
   * @calls { GET; {ostApiUrl}/init; OFFER_API }
   * @param {string} subscriptionId
   * @param {boolean} readOnly
   * @param {boolean} resetInit
   * @return {Promise<Basket>}
   */
  async getBasket({ subscriptionId, readOnly = false, resetInit = false }) {
    await this._checkCookiePromise();
    if (!subscriptionId) {
      throw new Error("Cannot create basket without 'subscriptionId'.");
    }

    const mutex = this._getOrCreateMutex(subscriptionId);
    const unlockMutex = await mutex.lock();

    let cache = readOnly
      ? this._ostCacheManager.getReadOnlyCache(subscriptionId)
      : this._ostCacheManager.getWritableCache(subscriptionId);
    if (cache === null) {
      cache = new OstCache({
        subscriptionId,
        readOnlyData: readOnly,
      });
      this._ostCacheManager.setCache(cache);
    }

    if (resetInit && !cache.readOnlyData) {
      cache.init = null;
      cache.update = null;
      cache.summary = null;
    }

    try {
      if (!cache.hasCatalog() && !cache.hasInit()) {
        const { catalog, basket } = await this._ostRequest.getInitWithCatalog(
          subscriptionId,
          readOnly,
        );
        cache.catalog = catalog;
        cache.init = basket;
      } else if (!cache.hasCatalog()) {
        // Standalone Catalog has to be always fetched from `getInitWithCatalog`, `readOnly` for not wasting new baskets in database
        const { catalog } = await this._ostRequest.getInitWithCatalog(subscriptionId, true);
        cache.catalog = catalog;
      } else if (!cache.hasInit()) {
        cache.init = await this._ostRequest.init(subscriptionId, readOnly);
      }

      return await this._createAugmentedBasket({
        catalogResponse: cache.catalog,
        initResponse: cache.init,
        updateResponse: cache.hasUpdate() ? cache.update : null,
        summaryResponse: cache.hasSummary() ? cache.summary : null,
        readOnly,
        subscriptionId,
      });
    } finally {
      unlockMutex();
    }
  }

  /**
   * Create new basket via `Parser` and then augment it with data sharing services.
   *
   * @param {Object} catalogResponse
   * @param {Object} initResponse
   * @param {?Object} updateResponse
   * @param {?Object} summaryResponse
   * @param {boolean} readOnly
   * @param {string} subscriptionId
   * @return {Promise<Basket>}
   * @private
   */
  async _createAugmentedBasket({
    catalogResponse,
    initResponse,
    updateResponse,
    summaryResponse,
    readOnly,
    subscriptionId,
  }) {
    const basket = Parser.createBasket({
      catalogResponse,
      initResponse,
      updateResponse,
      summaryResponse,
      readOnly,
      subscriptionId,
    });

    await this._dataSharingParser.augmentBasket(basket);

    await this._logBasket(basket);

    return basket;
  }

  /**
   * Log the basket contents in an abbreviated format.
   * Only log the basket if it changed (current basketId is different from the one last logged) and only
   * log how the basket's contents changed from the last time it was logged.
   *
   * Example:
   *  - You start with read-only basket A - it's logged in full
   *  - You navigate to a new page, you're still on basket A, and it didn't change - nothing is logged
   *  - You add a service a new writable basket B is created - it's logged in full, because it's a different basket
   *  - Update is called and basket B is changed - only the changed services are logged
   *  - You cancel the purchase, and you go back to basket A - basket A is logged as unchanged
   *
   * @param {Basket} basket
   * @return {Promise<void>}
   * @private
   */
  async _logBasket(basket) {
    const { subscriptionId, basketId, isReadOnly } = basket;
    const { sn } = (await this._customerDataManager.getCustomerData()).getSnDataBySubscriptionId(
      basket.subscriptionId,
    );

    const serviceLog = basket.services.map(service => {
      const logLine = [
        service.uuid,
        service.slug,
        service.isActive ? 'A' : 'NA',
        service.isEligible ? 'E' : 'NE',
        service.isVisible ? 'V' : 'NV',
      ];
      if (service.price.exists) {
        logLine.push(`P/${service.price.cycle ?? '-'}/${service.price.purchase ?? '-'}`);
      }
      if (service.originalPrice.exists && !service.originalPrice.isEqual(service.price)) {
        logLine.push(
          `OP/${service.originalPrice.cycle ?? '-'}/${service.originalPrice.purchase ?? '-'}`,
        );
      }

      return logLine.join(':');
    });

    const [lastLoggedBasketId, serviceLogCache] = this._basketLogCache.get(subscriptionId) ?? [
      null,
      new Map(),
    ];

    const cachedServiceLog = serviceLogCache.get(basketId) ?? null;

    // Identify changes against the cached version of given basketId
    let serviceLogDiff = [];
    if (!cachedServiceLog || serviceLog.length !== cachedServiceLog.length) {
      serviceLogDiff = serviceLog;
    } else {
      serviceLog.forEach((service, index) => {
        const cachedService = cachedServiceLog[index];
        if (service !== cachedServiceLog[index]) {
          serviceLogDiff.push(`${cachedService} > ${service}`);
        }
      });
    }

    // If given basketId is the last one logged, and it didn't change, no further log is necessary
    if (lastLoggedBasketId === basketId && serviceLogDiff.length === 0) {
      return;
    }

    const logString = `OST Basket; ${subscriptionId}; ${sn}; ${basketId}; ${isReadOnly ? `read-only` : 'writable'}`;

    if (serviceLogDiff.length === 0) {
      // If the contents are unchanged, shorten the log
      this._logger.info(`${logString}; unchanged`, 'infra-ost');
    } else if (serviceLogDiff.length < serviceLog.length) {
      // If there's some number of changes, log just the changes
      this._logger.info(`${logString}; changed\n${serviceLogDiff.join('\n')}`, 'infra-ost');
    } else {
      // If it's a new basket log the whole thing
      this._logger.info(`${logString}; new\n${serviceLog.join('\n')}`, 'infra-ost');
    }

    // Preserve the latest basket log
    serviceLogCache.set(basketId, serviceLog);
    this._basketLogCache.set(subscriptionId, [basketId, serviceLogCache]);
  }

  /**
   *
   * @calls { GET; {ostApiUrl}/update; OFFER_API }
   * @param {Basket} basket
   * @param {Service} service
   * @return {Promise<Basket>}
   */
  async addService({ basket, service }) {
    await this._checkCookiePromise();
    return this._basketServiceUpdate({
      basket,
      service,
      basketOperation: this._ostRequest.updateAdd.bind(this._ostRequest),
    });
  }

  /**
   *
   * @calls { GET; {ostApiUrl}/update; OFFER_API }
   * @param {Basket} basket
   * @param {Service} service
   * @return {Promise<Basket>}
   */
  async removeService({ basket, service }) {
    await this._checkCookiePromise();
    return this._basketServiceUpdate({
      basket,
      service,
      basketOperation: this._ostRequest.updateRemove.bind(this._ostRequest),
    });
  }

  /**
   *
   * @calls { GET; {ostApiUrl}/update; OFFER_API }
   * @param {Service} service
   * @param {Basket} basket
   * @return {Promise<Basket>}
   */
  async updateService({ basket, service }) {
    await this._checkCookiePromise();
    return this._basketServiceUpdate({
      basket,
      service,
      basketOperation: this._ostRequest.updateUpdate.bind(this._ostRequest),
    });
  }

  /**
   * Helper method with common logic for `addService`, `removeService` and 'updateService`.
   *
   * @calls { GET; {ostApiUrl}/get-order-summary; OFFER_API }
   * @param {Basket} basket
   * @param {Service} service
   * @param {function} basketOperation
   * @return {Promise<Basket>}
   * @private
   */
  async _basketServiceUpdate({ basket, service, basketOperation }) {
    const { subscriptionId } = basket;

    if (basket.isReadOnly) {
      throw new Error('Cannot modify readonly basket');
    }

    const cache = this._ostCacheManager.getWritableCache(subscriptionId);

    // Validate, if cache belongs to requested basket.
    // Prevent overwrite of newer (cached) basket data than was requested to update.
    if (!Ost._isCacheMatchingBasket(cache, basket)) {
      throw new Error('Cannot modify basket, newer data was found in cache');
    }

    const mutex = this._getOrCreateMutex(subscriptionId);
    const unlockMutex = await mutex.lock();

    try {
      const updateResponse = await basketOperation({
        subscriptionId,
        basketId: basket.basketId,
        itemId: service.id,
        itemCustomerId: service.customerOrderId,
        attributes: ReverseParser.mapAttributeValuesToAttributeIds(service),
      });
      const summaryResponse = await this._ostRequest.orderSummary(subscriptionId, basket.basketId);

      cache.update = updateResponse;
      cache.summary = summaryResponse;

      return await this._createAugmentedBasket({
        catalogResponse: cache.catalog,
        initResponse: cache.init,
        updateResponse: cache.update,
        summaryResponse: cache.summary,
        readOnly: false,
        subscriptionId,
      });
    } finally {
      unlockMutex();
    }
  }

  /**
   * Loads data for existing prima kids profile.
   *
   * To create new profile, proceed by activating separate Prima Kids profile service.
   *
   * @calls { GET; {ostApiUrl}/update; OFFER_API }
   * @calls { GET; {ostApiUrl}/get-order-summary; OFFER_API }
   * @param {Basket} basket
   * @param {string} sn
   * @returns {Promise<Basket>}
   */
  async loadPrimaKidsProfile({ basket, sn }) {
    await this._checkCookiePromise();
    if (basket.isReadOnly) {
      throw new Error('Cannot load Prima Kids profile on readOnly basket');
    }

    const { subscriptionId, basketId } = basket;
    const cache = this._ostCacheManager.getWritableCache(subscriptionId);

    // Validate, if cache belongs to requested basket.
    // Prevent overwrite of newer (cached) basket data than was requested to update.
    if (!Ost._isCacheMatchingBasket(cache, basket)) {
      throw new Error('Cannot modify basket, newer data was found in cache');
    }

    const mutex = this._getOrCreateMutex(subscriptionId);
    const unlockMutex = await mutex.lock();

    try {
      const updateResponse = await this._ostRequest.updateLoadPrimaKids({
        subscriptionId,
        basketId,
        primaKidsSn: sn,
      });
      const summaryResponse = await this._ostRequest.orderSummary(subscriptionId, basketId);

      // Update prices in cached init basket, they are changed by loading Prima Kids profile.
      // Basket updated by Prima Kids profile is considered "basic", "initial" and "not updated", basket.
      // Init response has little different content, so we are replacing only certain parts.
      cache.init.actualSubsidyMf = updateResponse.actualSubsidyMf;
      cache.init.minimalSubsidyMf = updateResponse.minimalSubsidyMf;
      cache.init.actualDiffSubsidyMf = updateResponse.actualDiffSubsidyMf;
      cache.init.shoppingCartPrices = updateResponse.shoppingCartPrices;

      cache.update = updateResponse;
      cache.summary = summaryResponse;

      return await this._createAugmentedBasket({
        catalogResponse: cache.catalog,
        initResponse: cache.init,
        updateResponse: cache.update,
        summaryResponse: cache.summary,
        readOnly: false,
        subscriptionId,
      });
    } finally {
      unlockMutex();
    }
  }

  /**
   * Validates attributes of service for given subscriptionId.
   * Returns object with Map of attributes with error message.
   * ReadOnly attributes are not validated.
   *
   * @calls { GET; {ostApiUrl}/check-attribute-inputs; OFFER_API }
   * @param {string} subscriptionId
   * @param {Service} service
   * @param {?string} primaKidsSn - optional, if the validation relates to kid's services
   * @returns {Promise<{isValid: boolean, attributes: Map<string, {errorMessage: string}>}>}
   */
  async validateAttributes({ subscriptionId, service, primaKidsSn = null }) {
    await this._checkCookiePromise();
    const attributes = ReverseParser.mapAttributeValuesToAttributeIds(service);
    if (!attributes.size) {
      return {
        isValid: true,
        attributes: new Map(),
      };
    }

    const checkAttributeInputsResponse = await this._ostRequest.checkAttributeInputs(
      subscriptionId,
      service.uuid,
      attributes,
      primaKidsSn,
    );

    return Parser.createValidationResponse(checkAttributeInputsResponse);
  }

  /**
   * Returns order summary for given subscriptionId.
   * Order summary response is cached from last update operation and is not needed to be called again.
   *
   * @param {Basket} basket
   * @returns {Promise<Summary>}
   */
  async getOrderSummary({ basket }) {
    await this._checkCookiePromise();
    const { subscriptionId } = basket;
    const cache = this._ostCacheManager.getWritableCache(subscriptionId);

    // Validate, if cache belongs to requested basket.
    if (!Ost._isCacheMatchingBasket(cache, basket)) {
      throw new Error('Newer basket data was found in cache');
    }

    if (!cache.hasSummary()) {
      throw new Error('Cannot return summary for unmodified basket');
    }

    return Parser.createSummary(cache.summary);
  }

  /**
   * Submits order and clears cache for subscriptionId.
   *
   * @calls { GET; {ostApiUrl}/submit-order; OFFER_API }
   * @param {string} subscriptionId
   * @param {string} basketId
   * @returns {Promise<void>}
   */
  async submitOrder({ subscriptionId, basketId }) {
    await this._checkCookiePromise();
    await this._ostRequest.submitOrder(subscriptionId, basketId);
    this._ostCacheManager.clear(subscriptionId);
  }

  /**
   * Clears cache for given subscriptionId.
   *
   * *Function is async, because `_registerMessageBusListener` expects promise to chain*
   *
   * @param {string} subscriptionId
   * @returns {Promise<void>}
   */
  async clearCache({ subscriptionId }) {
    await this._checkCookiePromise();
    this._ostCacheManager.clear(subscriptionId);
  }

  /**
   * Checks, if subscriptionId is supported by OST API.
   *
   * Returns information about whether the current tariff/program is supported by OST,
   * and in case it isn't, whether it's supported in the old Ecare.
   *
   * @calls { GET; {ostApiUrl}/is-supported; OFFER_API }
   * @param {string} subscriptionId
   * @returns {Promise<{isSupported: boolean, isOldEcareAvailable: boolean}>}
   */
  async checkOstSupport({ subscriptionId }) {
    await this._checkCookiePromise();
    let supportInfo = this._ostCacheManager.getSupportCache(subscriptionId);
    if (supportInfo === null) {
      supportInfo = await this._ostRequest.isSupported(subscriptionId);
      this._ostCacheManager.setSupportCache(subscriptionId, supportInfo);
    }

    return {
      isSupported: supportInfo.isSupported === 'Y',
      isOldEcareAvailable: supportInfo.isSupported === 'N' && supportInfo.reason !== 'NEW',
    };
  }

  /**
   * Checks if basket matches cached data.
   * Mismatch means, that:
   * - cached data or basket does not exists
   * - cached data are probably newer than basket and they cannot be used together.
   *
   * @param {OstCache} cache
   * @param {Basket} basket
   * @returns {boolean}
   */
  static _isCacheMatchingBasket(cache, basket) {
    return cache !== null && basket !== null && cache.getBasketId() === basket.basketId;
  }

  /**
   * Creates or returns existing mutex object.
   *
   * @param {string} mutexId
   * @returns {object}
   * @private
   */
  _getOrCreateMutex(mutexId) {
    const key = mutexId.toString();
    if (this._mutexList.has(key)) {
      return this._mutexList.get(key);
    }

    this._mutexList.set(key, new Mutex());
    return this._mutexList.get(key);
  }

  /**
   * Checks whether customer has any love order in progress or failed.
   *
   * @calls { GET; {ostApiUrl}/get-failed-love; OFFER_API }
   * @calls { GET; {ostApiUrl}/get-semifinished-love; OFFER_API }
   * @param {string} subscriptionId
   * @returns {Promise<{hasFailedLoveOrder: boolean, hasInProgressLoveOrder: boolean}>}
   */
  async getPreviousLoveOrderInfo(subscriptionId) {
    await this._checkCookiePromise();
    const failedLoveOrderInfoPromise = this._ostRequest.failedLove(subscriptionId);
    const inProgressLoveOrderInfoPromise = this._ostRequest.semifinishedLove(subscriptionId);

    const failedLoveOrderInfo = await failedLoveOrderInfoPromise;
    const inProgressLoveOrderInfo = await inProgressLoveOrderInfoPromise;
    const twoDaysAgo = new Date().setDate(new Date().getDate() - 2);

    return {
      hasFailedLoveOrder:
        Array.isArray(failedLoveOrderInfo) &&
        failedLoveOrderInfo.some(
          failedOrder => new Date(failedOrder.date_create) >= new Date(twoDaysAgo),
        ),
      hasInProgressLoveOrder:
        Array.isArray(inProgressLoveOrderInfo) && inProgressLoveOrderInfo.length > 0,
    };
  }

  /**
   *
   * @calls { GET; {ostApiUrl}/submit-love; OFFER_API }
   * @param {string} subscriptionId
   * @param {Love} love
   * @return {Promise<object>}
   */
  async postNewLoveGroups({ subscriptionId, love }) {
    await this._checkCookiePromise();
    return this._ostRequest.postNewLoveGroups({
      subscriptionId,
      members: love.love17GrowGroups.map(group => ({
        voice_sn: formatSn(
          group.potentialMembers.find(groupMember => !groupMember.isFixed).sn,
          false,
        ),
        fix_sn: formatSn(group.potentialMembers.find(groupMember => groupMember.isFixed).sn, false),
      })),
    });
  }
}
